/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import {
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { PidxLoaderModeEnum, PidxPdsModalWrapperService } from '@myprofile-ui/pidx-ui-components'
import { PidxErrorService } from '@myprofile-ui/pidx-utils'
import { ToastManager } from '@porsche-design-system/components-angular'
import { EMPTY, lastValueFrom, of } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { MyProfileEmailWidgetTranslations } from './interfaces/myprofile-email-widget.interfaces'
import { MyProfileEmailWidgetService } from './services/myprofile-email-widget.service'
import { RESTRICT_GLOBAL_UI_CHANGES } from '../../../pidx-utils/src/lib/helpers/restrictGlobalUIChanges.injectionToken'
import { EmailCommunicationDto, EmailService } from '../../../../apps/myprofile-ui/src/app/services/email/email.service'

export enum CallingContainerMode {
  PRIMARY = 'PRIMARY',
  ADDITIONAL = 'ADDITIONAL',
  ADD_EMAIL = 'ADD_EMAIL',
}

export const CONFLICT_EMAIL_ALREADY_EXISTS_IN_PROFILE = 'CONFLICT_EMAIL_ALREADY_EXISTS_IN_PROFILE'

@Component({
  selector: 'myprofile-email-widget',
  templateUrl: './myprofile-email-widget.component.html',
  host: { class: 'profile:block profile:relative profile:break-words' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileEmailWidgetComponent implements OnInit {
  private emailService = inject(EmailService)
  private pidxErrorService = inject(PidxErrorService)
  private toastManager = inject(ToastManager)
  private pidxPdsModalWrapperService = inject(PidxPdsModalWrapperService)
  private elementRef = inject(ElementRef)

  public MAX_EMAILS = 5

  @Input() translations!: MyProfileEmailWidgetTranslations
  @Input() country!: string
  @Input() locale!: string
  @Input() isCnUser = false

  /**
   * When `true`, the widget announces its own initial-load status via a polite
   * live region using the host-supplied `translations.general.loadingStatus`
   * string. Default `false` keeps the widget silent during initial load so a
   * host page can orchestrate one global announcement; set to `true` for
   * standalone embeddings.
   *
   * Does not gate the saving overlay (always announced because it is
   * user-initiated).
   */
  public announceLoading = input(false, { transform: booleanAttribute })

  @Output() goToPorscheIdChangePageEvent = new EventEmitter()
  @Output() emailStateChanged = new EventEmitter()

  public isLoading = signal(true)
  public isRefreshing = signal(false)

  public primaryEmail = signal<EmailCommunicationDto | undefined>(undefined)
  public allEmails = signal<EmailCommunicationDto[]>([])
  public additionalEmails = signal<EmailCommunicationDto[]>([])
  public errorKey = signal<string | null>(null)
  public emailAlreadyExistsError = signal(false)

  public emailObjectBeingEdited = signal<EmailCommunicationDto | null>(null)
  public shouldShowEditor = signal(false)
  /** Holds the (created/updated) unverified e-mail currently being verified via OTP, or `null` when not verifying. */
  public emailBeingVerified = signal<EmailCommunicationDto | null>(null)

  /** Remember which row triggered edit mode so focus can return to it after the editor closes (WCAG 2.4.3). */
  private lastEditedFromButtonId: string | null = null
  /** Page-level fallback id (e.g. completion-status CTA) used when neither the row button nor the add-email button is rendered. */
  private externalFocusReturnId: string | null = null

  public CallingContainerMode = CallingContainerMode
  public activeCallingContainerMode = signal(CallingContainerMode.PRIMARY)
  public restrictGlobalUIChanges = inject<boolean>(RESTRICT_GLOBAL_UI_CHANGES)

  protected readonly PidxLoaderModeEnum = PidxLoaderModeEnum

  public async ngOnInit() {
    await this.fetchEmails()
    this.isLoading.set(false)
  }

  public showList(emailCommunicationDtos: EmailCommunicationDto[], emailObjectBeingEdited: EmailCommunicationDto | null) {
    if (!emailObjectBeingEdited) {
      return true
    }

    return !emailCommunicationDtos.find((emails) => emails.emailId === emailObjectBeingEdited.emailId)
  }
  public async fetchEmails(): Promise<boolean> {
    return lastValueFrom(
      this.emailService.getEmailsForProfile(this.country, this.locale).pipe(
        tap((emailAddresses) => {
          // Set all E-Mail
          this.allEmails.set(emailAddresses)

          // Set only the primary E-Mail
          this.primaryEmail.set(emailAddresses.find((anAddress) => anAddress.isStandard))

          // Set all additional E-Mails (sort by id and then put the one with the porsche id in
          // front in case there is one available
          this.additionalEmails.set(MyProfileEmailWidgetService.sortEmails(emailAddresses.filter((anAddress) => !anAddress.isStandard)))
        }),
        map(() => true),
        catchError((error) => {
          this.errorKey.set(this.pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error))
          return of(false)
        }),
      ),
    )
  }

  public enterEditMode(
    emailObject: EmailCommunicationDto | null,
    callingContainerMode = CallingContainerMode.PRIMARY,
    externalFocusReturnId?: string,
  ) {
    this.emailObjectBeingEdited.set(emailObject)
    this.errorKey.set(null)
    this.emailAlreadyExistsError.set(false)
    this.activeCallingContainerMode.set(callingContainerMode)
    this.shouldShowEditor.set(true)
    this.lastEditedFromButtonId = emailObject ? `email-edit-btn-${emailObject.emailId}` : 'addEmailAddressButton'
    if (externalFocusReturnId) {
      this.externalFocusReturnId = externalFocusReturnId
    }

    if (callingContainerMode === CallingContainerMode.PRIMARY) {
      // Prefer the namespaced "change Porsche ID" link if it is rendered, otherwise
      // fall back to the editor's email input (WCAG 2.4.3 — never silently lose focus).
      const porscheIdLinkId = emailObject ? `goToChangePorscheIdPage-${emailObject.emailId}` : null
      this.setFocusWithFallback([porscheIdLinkId, 'email-input'])
    } else if (callingContainerMode === CallingContainerMode.ADDITIONAL || callingContainerMode === CallingContainerMode.ADD_EMAIL) {
      this.setFocusOnElementId('email-input')
    }
  }

  public showEditorForEmails(emailObjects: (EmailCommunicationDto | undefined)[]) {
    const emailObjectBeingEdited = this.emailObjectBeingEdited()
    if (!emailObjectBeingEdited) {
      return false
    }

    return emailObjects.find((emailObject) => emailObject?.emailId === emailObjectBeingEdited.emailId)
  }

  public async deleteEmailAddress(emailObject: EmailCommunicationDto, callingContainerMode = CallingContainerMode.PRIMARY) {
    this.isRefreshing.set(true)
    this.activeCallingContainerMode.set(callingContainerMode)
    try {
      await lastValueFrom(this.emailService.deleteEmailFromProfile(this.country, this.locale, emailObject.emailId ?? ''))

      const refreshed = await this.fetchEmails()
      if (!refreshed) {
        return
      }

      const reassigned = await this.reassignStandardIfNeeded()
      if (!reassigned) {
        return
      }

      this.emailStateChanged.emit()
      this.leaveEditMode()
    } catch (error) {
      this.errorKey.set(this.pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error as HttpErrorResponse))
    } finally {
      this.isRefreshing.set(false)
    }
  }

  public hasVerifiedReplacement(): boolean {
    return this.allEmails().some((anAddress) => !anAddress.isStandard && anAddress.isVerified)
  }

  private async reassignStandardIfNeeded(): Promise<boolean> {
    const nextStandard = MyProfileEmailWidgetService.pickNextStandard(this.allEmails())
    if (!nextStandard?.emailId) {
      return true
    }
    const { emailId } = nextStandard
    const payload = { ...nextStandard, isStandard: true }
    delete payload.emailId
    await lastValueFrom(this.emailService.updateEmailFromProfile(this.country, this.locale, emailId, payload))
    return this.fetchEmails()
  }

  public addNewEmailAddress(emailObject: EmailCommunicationDto, callingContainerMode = CallingContainerMode.PRIMARY) {
    this.isRefreshing.set(true)
    this.emailAlreadyExistsError.set(false)
    this.activeCallingContainerMode.set(callingContainerMode)
    this.emailService
      .createEmailForProfile(this.country, this.locale, emailObject)
      .pipe(
        tap(() => {
          this.emailStateChanged.emit()
          if (!emailObject.isStandard) {
            this.fetchEmails()
            this.leaveEditMode()
            this.toastManager.addMessage({ text: this.translations.general.successMessage, state: 'success' })
          } else {
            this.enterVerificationStep(emailObject)
          }
          this.isRefreshing.set(false)
        }),
        catchError((error) => {
          this.handleSaveError(error)
          this.isRefreshing.set(false)
          return EMPTY
        }),
      )
      .subscribe()
  }

  public async updateExistingEmailAddress(emailObject: EmailCommunicationDto, callingContainerMode = CallingContainerMode.PRIMARY) {
    this.isRefreshing.set(true)
    this.emailAlreadyExistsError.set(false)
    this.activeCallingContainerMode.set(callingContainerMode)

    const { emailId } = emailObject
    const payload = { ...emailObject }
    delete payload.emailId

    await this.emailService
      .updateEmailFromProfile(this.country, this.locale, emailId ?? '', payload)
      .pipe(
        tap(() => {
          if (emailObject.isStandard && !emailObject.isVerified) {
            this.enterVerificationStep(emailObject)
          } else {
            this.emailStateChanged.emit()
            this.fetchEmails()
            this.leaveEditMode()
            this.toastManager.addMessage({ text: this.translations.general.successMessage, state: 'success' })
          }
          this.isRefreshing.set(false)
        }),
        catchError((error) => {
          this.handleSaveError(error)
          this.isRefreshing.set(false)
          return EMPTY
        }),
      )
      .subscribe()
  }

  public goToPorscheIdChangePage() {
    this.goToPorscheIdChangePageEvent.emit()
  }

  private handleSaveError(error: HttpErrorResponse | null): void {
    if (error?.error?.errorCode === CONFLICT_EMAIL_ALREADY_EXISTS_IN_PROFILE) {
      this.emailAlreadyExistsError.set(true)
      return
    }
    this.errorKey.set(this.pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error))
  }

  // checks if an additional or new email is being edited
  public editAdditionalOrAddNewIsOpen() {
    return this.showEditorForEmails(this.additionalEmails()) || (this.shouldShowEditor() && !this.emailObjectBeingEdited())
  }

  // checks if a primary or new email is being edited
  public editPrimaryOrAddNewIsOpen() {
    return this.showEditorForEmails([this.primaryEmail()]) || (this.shouldShowEditor() && !this.emailObjectBeingEdited())
  }

  /**
   * Refreshes the address list to obtain the persisted (unverified) e-mail — including its id — and switches the
   * widget into the OTP verification step. The dedicated verification component requests the code on init.
   */
  public async enterVerificationStep(email: EmailCommunicationDto) {
    await lastValueFrom(
      this.emailService.getEmailsForProfile(this.country, this.locale).pipe(
        tap((emailAddresses) => {
          this.allEmails.set(emailAddresses)
        }),
        catchError((error) => {
          this.errorKey.set(this.pidxErrorService.getErrorKeyForHttpErrorResponseByCode(error))
          return of([])
        }),
      ),
    )
    const mailToVerify = this.allEmails().find((value) => value.email === email.email && !value.isVerified)
    if (!mailToVerify?.emailId) {
      return
    }
    this.emailBeingVerified.set(mailToVerify)
  }

  /**
   * Success handler for the OTP verification component: refresh the list, notify the host, show the success toast and
   * leave the verification/edit step.
   */
  public async onEmailVerified() {
    this.emailStateChanged.emit()
    await this.fetchEmails()
    this.toastManager.addMessage({ text: this.translations.verification.verificationSuccessMessage, state: 'success' })
    this.leaveEditMode()
  }

  /**
   * Reload handler for the OTP verification component: used when the backend reports the e-mail is already confirmed
   * or no longer exists. Refreshes the list and returns to the list view without a toast.
   */
  public async onVerificationReload() {
    this.emailStateChanged.emit()
    this.errorKey.set(null)
    await this.fetchEmails()
    if (this.errorKey()) return
    this.leaveEditMode()
  }

  public async cancelVerification(email: EmailCommunicationDto) {
    const result = await this.pidxPdsModalWrapperService.openModal({
      title: this.translations.modal.cancelVerificationModalHeadline,
      body: this.translations.modal.cancelVerificationModalText,
      btnPrimary: this.translations.modal.cancelVerificationButtonPrimary,
      btnGhost: this.translations.modal.cancelVerificationButtonCancel,
    })
    if (result) {
      await this.fetchEmails()
      const emailToBeVerified = this.allEmails().find((value) => value.email === email.email)
      if (emailToBeVerified && !emailToBeVerified.isVerified) {
        this.deleteEmailAddress(emailToBeVerified)
      }
      this.leaveEditMode()
    }
  }

  public leaveEditMode() {
    this.errorKey.set(null)
    this.emailAlreadyExistsError.set(false)
    this.activeCallingContainerMode.set(CallingContainerMode.PRIMARY)
    this.emailObjectBeingEdited.set(null)
    this.shouldShowEditor.set(false)
    this.emailBeingVerified.set(null)

    // Return focus to the originating row button so keyboard users keep their place (WCAG 2.4.3).
    // Fallbacks: the page-level CTA when the row button isn't rendered (e.g. add-email from CTA with
    // no list), then the in-widget Add-email button which covers the delete-from-list flow where the
    // row that previously held focus has just been removed from the DOM.
    const returnTargetId = this.lastEditedFromButtonId
    const externalId = this.externalFocusReturnId
    this.lastEditedFromButtonId = null
    this.externalFocusReturnId = null
    this.setFocusWithFallback([returnTargetId, externalId, 'addEmailAddressButton'])
  }

  private setFocusOnElementId(id: string) {
    setTimeout(() => {
      const forFocus = this.findFocusableById(id)
      if (forFocus) {
        forFocus.focus()
      }
    }, 500)
  }

  /**
   * Focus the first id in the list that resolves to a real element. Allows a graceful fallback
   * (e.g. when the Porsche-ID link is not rendered for the current row) so focus is never lost.
   */
  private setFocusWithFallback(ids: Array<string | null | undefined>) {
    setTimeout(() => {
      for (const id of ids) {
        if (!id) continue
        const candidate = this.findFocusableById(id)
        if (candidate) {
          candidate.focus()
          return
        }
      }
    }, 500)
  }

  private findFocusableById(id: string): HTMLElement | null {
    const root = (this.elementRef?.nativeElement as HTMLElement) ?? null
    // Use a manual selector escape (CSS.escape is not available in jsdom) and fall
    // back to global lookup so PDS web components rendered outside the host root
    // still resolve.
    const escapedId = id.replace(/([^a-zA-Z0-9_-])/g, '\\$1')
    const scoped = root?.querySelector(`#${escapedId}`) as HTMLElement | null
    return scoped ?? (document.getElementById(id) as HTMLElement | null)
  }
}
